import { Injectable } from '@angular/core';
import { LOCAL_STORAGE_KEY_ACCESS_TOKEN, VERSION, VALIDATE, LOCAL_STORAGE_KEY_FORCE_FILTER_VALIDATION } from '../const';
import { FilterService } from './filter.service';
import { FILTER_DESCRIPTION } from '../../game';
import { ToastService } from './toast.service';

export interface PoeFilter {
  id: string;
  filter_name: string;
  realm: string;
  description: string;
  version: string;
  type: string;
  public?: boolean;
  filter?: string;
  validation?: {
    valid: boolean;
    version?: string;
    validated?: string;
  };
}

const LOCAL_STORAGE_KEY_USE_MOCK = 'useMockService';

@Injectable({
  providedIn: 'root'
})
export class PoeApiService {
  baseUrl = 'https://api.pathofexile.com';
  userAgent = 'OAuth poe2filter/1 (contact: vincent.sels@outlook.com)';

  constructor(private filterService: FilterService, private toastService: ToastService) {
    // TODO: wtf is this; why did i do this?
    // if (localStorage.getItem(LOCAL_STORAGE_KEY_USE_MOCK) === 'true') {
    //   return new PoeApiMockService(filterService);
    // }
  }

  async getFilters(): Promise<PoeFilter[]> {
    const token = localStorage.getItem(LOCAL_STORAGE_KEY_ACCESS_TOKEN);

    const response = await fetch(`${this.baseUrl}/item-filter`, {
      headers: {
        'User-Agent': this.userAgent,
        'Authorization': `Bearer ${token}`
      }
    });

    const data = await response.json();
    this.throwIfError(data);

    return data.filters.map((filter: any) => filter as PoeFilter);
  }

  async getFilter(id: string): Promise<PoeFilter> {
    const token = localStorage.getItem(LOCAL_STORAGE_KEY_ACCESS_TOKEN);

    const response = await fetch(`${this.baseUrl}/item-filter/${id}`, {
      headers: {
        'User-Agent': this.userAgent,
        'Authorization': `Bearer ${token}`
      }
    });

    const data = await response.json();
    this.throwIfError(data);

    return data.filter as PoeFilter;
  }

  async createFilter(name: string, filter: string, realm: string, type?: string): Promise<PoeFilter> {
    const token = localStorage.getItem(LOCAL_STORAGE_KEY_ACCESS_TOKEN);

    const validate = this.shouldValidateFilter();

    const body: any = {
      filter_name: name,
      description: FILTER_DESCRIPTION,
      version: VERSION,
      realm,
      filter
    };
    if (type) body.type = type;

    const response = await fetch(`${this.baseUrl}/item-filter${validate ? '?validate=true' : ''}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'User-Agent': this.userAgent,
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(body)
    });

    const data = await response.json();
    this.throwIfError(data);

    return data.filter as PoeFilter;
  }

  async updateFilter(filterId: string, filterName: string, filter: string, realm: string, type?: string): Promise<PoeFilter> {
    const token = localStorage.getItem(LOCAL_STORAGE_KEY_ACCESS_TOKEN);

    const validate = this.shouldValidateFilter();

    const body: any = {
      filter_name: filterName,
      description: FILTER_DESCRIPTION,
      version: VERSION,
      filter,
    };
    if (type) body.type = type;

    const response = await fetch(`${this.baseUrl}/item-filter/${filterId}${validate ? '?validate=true' : ''}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'User-Agent': this.userAgent,
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(body)
    });

    if (response.status === 404) {
      if (confirm('This filter seems to have been removed on your account, or you are logged in with another account. Do you want to (re-)create the filter on this account?')) {
        return this.createFilter(filterName, filter, realm, type).then((recreatedFilter: PoeFilter) => {
          this.filterService.setFilterId(filterName, recreatedFilter.id);

          return recreatedFilter;
        });
      } else {
        throw new Error('This filter was not found on your account. Are you logged in with the correct account?');
      }
    }

    const data = await response.json();
    this.throwIfError(data);

    return data.filter as PoeFilter;
  }

  shouldValidateFilter(): boolean {
    return VALIDATE || !!localStorage.getItem(LOCAL_STORAGE_KEY_FORCE_FILTER_VALIDATION);
  }

  throwIfError(data: any) {
    if (data.error) {
      let error = data.error;
      if (error?.code === 0) {
        this.toastService.raise('warning', 'There will be a delay before your filter will be available in the game.');
        throw new FilterUpdateDelayedError();
      } else {
        let errorMessage = error?.message || 'Unknown';
        if (error.error_description) {
          errorMessage += ' - ' + error.error_description;
        }
        if (error.fields) {
          errorMessage += ' - ' + JSON.stringify(error.fields);
        }
        this.toastService.raise('error', errorMessage);
        throw new PoeApiReportedError(errorMessage);
      }
    }
  }
}

/** Thrown after an error toast was already shown via {@link PoeApiService.throwIfError}. */
export class PoeApiReportedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PoeApiReportedError';
  }
}

export class FilterUpdateDelayedError extends Error {
  constructor() {
    super('This filter has been updated multiple times in the last 10 minutes, there will be a delay before your most recent changes can be synced to the game client.');
  }
}
